<template>
  <div class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden pb-20">
    <div
      class="w-full h-full flex items-center justify-center mt-40"
      v-if="loading"
    >
      <span class="loading loading-spinner loading-lg text-primary"></span>
    </div>
    <template v-else>
      <ThreadsNav :show-center="false" :show-back="true" />
      <div class="overflow-auto">
        <div class="px-5 mt-5 overflow-hidden w-full">
          <div class="relative card card-compact shadow-xl">
            <img
              src="https://www.any-mate.com/images/drawing-202502-12-c7703be1-7738-46b1-9652-1be30df25f7e.jpg/public?x-oss-process=image/format,webp"
              crossorigin="anonymous"
              alt=""
              class="w-full object-cover"
            />
            <div
              class="absolute bottom-0 left-0 right-0 h-[174px] bg-gradient-to-t from-black/50 to-transparent"
            />

            <div
              class="absolute top-4 left-4 right-4 bottom-4 bg-base-300 bg-opacity-90 rounded-box p-4 text-[10px]"
            >
              <div class="flex items-center gap-1 mb-4">
                <img
                  :src="dungeon?.mate?.mediaUrl"
                  alt=""
                  class="h-[30px] aspect-[3/4] rounded-lg"
                  crossorigin="anonymous"
                />

                <span>{{ $t("Dungeons.detail.host") }}</span>
                <span class="text-primary font-medium">
                  {{ dungeon?.mate?.name }}
                </span>
                <span>{{ $t("Dungeons.detail.says") }}</span>
              </div>

              <p class="text-primary leading-[14px]">
                {{ $t("Dungeons.detail.guide") }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2 px-5 my-1">
          <div class="flex items-center gap-2 text-[10px]">
            <div class="flex items-center justify-between w-full">
              <div class="flex items-center gap-2">
                <div class="text-base-content/50 font-medium">
                  {{ $t("Dungeons.room") }} #{{ dungeon?.id }}
                </div>
                <div
                  class="badge badge-sm"
                  :class="{
                    'badge-warning':
                      dungeon?.status === ScriptGameRoomStatus.Recruiting ||
                      dungeon?.status === ScriptGameRoomStatus.WaitingToStart,
                    'badge-success':
                      dungeon?.status === ScriptGameRoomStatus.InProgress,
                  }"
                >
                  {{ $t(`Dungeons.status.${ScriptGameRoomStatus.Recruiting}`) }}
                </div>
              </div>
            </div>
          </div>
          <h2 class="text-lg font-bold">
            {{ dungeon?.name }}
          </h2>
          <p class="text-xs text-base-content text-opacity-60">
            {{ dungeon?.shortDefinition }}
          </p>
          <div class="text-xs">
            <span class="text-base-content/50 font-medium mr-1">
              {{ $t("Dungeons.detail.scene") }}
            </span>
            <span class="text-primary">
              {{ $t(`Dungeons.scene.${dungeon?.chatScene?.name}`) }}
            </span>
          </div>
          <div class="text-xs">
            <span class="text-base-content/50 font-medium mr-1">
              {{ $t("Dungeons.detail.mode") }}
            </span>
            <span class="text-primary">
              {{ $t("Dungeons.detail.scoreRanking") }}
            </span>
          </div>
          <div class="card bg-base-200 p-4 text-center mt-3">
            <h4 class="text-base font-semibold">
              <span class="text-primary">
                {{ $t("Dungeons.detail.duration") }} {{ dungeon?.duration }}
                {{ $t("Dungeons.detail.minutes") }}
              </span>
            </h4>
            <div class="divider my-1"></div>
            <p class="text-xs flex justify-between">
              <span class="text-base-content/50 font-medium"
                >{{ $t("Dungeons.detail.start") }}
              </span>
              <span class="text-base-content">
                {{ moment(dungeon?.dtStart).format("YYYY-MM-DD HH:mm") }}
              </span>
            </p>
            <p class="text-xs flex justify-between">
              <span class="text-base-content/50 font-medium"
                >{{ $t("Dungeons.detail.end") }}
              </span>
              <span class="text-base-content">
                {{
                  moment(dungeon?.dtStart)
                    .add(dungeon?.duration, "minutes")
                    .format("YYYY-MM-DD HH:mm")
                }}
              </span>
            </p>
          </div>
        </div>

        <div class="stats stats-vertical w-full px-5">
          <div class="stat p-2 flex items-center">
            <div class="stat-title">{{ $t("Dungeons.detail.remaining") }}</div>
            <div class="stat-value text-lg text-primary">
              {{ dungeon.maxPlayers! - (dungeon.playerzz?.length || 0) }}
            </div>
          </div>
        </div>

        <div class="card bg-base-200 mx-5 my-3 p-4">
          <div class="w-full flex flex-wrap gap-2">
            <div
              v-for="serial in dungeon.maxPlayers"
              :key="serial"
              :class="[
                'w-[40px] h-12 overflow-hidden cursor-pointer rounded-box flex flex-col flex-shrink-0 border-2 border-dashed p-1',
                dungeon.playerzz.length >= serial
                  ? 'border-primary'
                  : 'border-secondary border-opacity-50',
              ]"
            >
              <DungeonsSpriteAnimation
                v-if="dungeon.playerzz[serial - 1]?.mateSprite"
                :animations="
                  dungeon.playerzz[serial - 1]?.mateSprite!.animationzz!
                "
              />
              <div v-else>
                <img
                  src="https://www.any-mate.com/images/drawing-202502-14-3a0e499d-1ae5-45b8-bc55-0a7acccda837.png/public?x-oss-process=image/format,webp"
                  alt=""
                  crossorigin="anonymous"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="px-5">
          <h3 class="text-lg font-semibold mb-2">
            {{ $t("Dungeons.detail.npc") }}
          </h3>

          <div
            class="carousel carousel-center w-full p-1 space-x-2 rounded-box"
          >
            <div
              v-for="(item, idx) in dungeon.chatScene?.characterzz?.filter(
                (v) => [1, 3].includes(v.role)
              )"
              :key="item.id"
              class="carousel-item overflow-hidden rounded-box flex flex-shrink-0 items-center gap-2 card bg-base-200 p-2"
              @click="clickCharacter(item)"
            >
              <div class="w-[50px]">
                <div class="avatar">
                  <div class="w-full rounded-box">
                    <img
                      :src="item.mate?.mediaUrl"
                      alt=""
                      crossorigin="anonymous"
                    />
                  </div>
                </div>
                <div class="line-clamp-1 text-center text-xs mt-1 font-medium">
                  {{ item.mate?.name }}
                </div>
              </div>
              <!-- NPC description commented out in original code -->
            </div>
          </div>
        </div>

        <div class="bg-base-300 p-3 fixed bottom-0 left-0 right-0 h-auto z-50">
          <div
            v-if="!user.isLogin"
            class="flex items-center gap-2 justify-center w-full"
          >
            <button
              class="btn w-full text-base-content"
              @click="user.loginModal.show = true"
            >
              <span>{{ $t("Dungeons.detail.loginToEnroll") }}</span>
            </button>
          </div>
          <div v-else-if="true" class="flex items-center gap-2 w-full">
            <button
              class="btn flex-1"
              @click="dungeonStore.showSelectMate(dungeon, 'trial')"
            >
              <span>{{ $t("Buttons.trial") }}</span>
            </button>
            <button
              class="btn btn-primary flex-1 gap-1"
              @click="dungeonStore.showSelectMate(dungeon, 'enroll')"
            >
              <span>{{ $t("Buttons.enroll") }}</span>
              <div>1</div>
              <CrystalIcon class="ml-0.5" />
            </button>
          </div>
          <div
            v-else
            class="text-base-content/50 text-base text-center font-semibold w-full"
          >
            <div class="alert alert-success">
              <span>You have enrolled.</span>
              <span>Game will start in 10 minutes.</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <SelectMateSpritePopup
      :show="dungeonStore.showSelectSpritePopup"
      :mate-id="dungeonStore.selectedMateId"
      @update:show="dungeonStore.showSelectSpritePopup = $event"
      @update:selectedSprite="dungeonStore.onSelectSprite"
    />
  </div>
</template>

<script setup lang="ts">
import { ScriptGameRoom } from "@/types/game";
import { ScriptGameRoomStatus } from "@/enums/game";
import { useGlobalUser } from "@/stores/globalUser";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useDungeon } from "@/stores/dungeon";
import moment from "moment-timezone";
import { useRouter } from "vue-router";
import { ChatSceneCharacter } from "@/types/chat";
const user = useGlobalUser();
const router = useRouter();
const route = useRoute();
const dungeonStore = useDungeon();
const loading = ref(false);
const dungeon = computed<ScriptGameRoom>(() => dungeonStore.dungeonRoom);

const clickCharacter = (item: ChatSceneCharacter) => {
  router.push({
    name: "mateHome",
    params: {
      uuid: item.mate?.uuid,
    },
  });
};

onMounted(async () => {
  const uuid: string = route.params.uuid as string;
  if (
    !dungeonStore.dungeonRoom?.uuid ||
    dungeonStore.dungeonRoom.uuid !== uuid
  ) {
    loading.value = true;
    await dungeonStore.fetchDungeonRoom(uuid);
    loading.value = false;
  } else {
    dungeonStore.fetchDungeonRoom(uuid);
  }
});
</script>

<style scoped>
.status-1,
.status-2 {
  background: #fed500;
}

.status-3 {
  background: #00fe43;
}
</style>
